import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Copy, Gift, LogOut, Pencil, Settings, Star, Trophy, Sparkles } from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import {
  CoinRainOverlay,
  createCoinParticles,
  playDailyRewardSound,
  DEFAULT_REWARD_COIN_SRC,
} from '../lib/rewardClaimCelebration';
import { getPlayerLeague } from './lobbyHubShared';
import {
  MAX_LEVEL,
  TROPHY_ROAD_MILESTONES,
  cumulativeRespectPoints,
  rankByLevel,
  trophyScoreToRoadProgressPx,
} from '../game/respectProgression';

const API_BASE_URL = getApiBaseUrl();

const AVATAR_MAX_SIDE = 512;
const AVATAR_JPEG_QUALITY = 0.82;
const MAX_DATA_URL_CHARS = 1_200_000;
const DEFAULT_GUEST_AVATAR_URL = `${process.env.PUBLIC_URL || ''}/guest-avatar.png`;
const FALLBACK_GUEST_AVATAR_URL = `${process.env.PUBLIC_URL || ''}/avatar-for-bot.png`;
const KEY_TRANSITION_RANKS = Object.freeze({
  5: 'Мужик',
  8: 'Босяк',
  12: 'Бродяга',
  15: 'Вор',
});

/** Читаемый ID для отображения (группы по 3 символа). */
export function formatPublicPlayerId(id) {
  if (id == null || id === '') return '—';
  const s = String(id);
  if (s === 'dev-local' || (typeof s === 'string' && s.startsWith('dev_'))) return 'DEV · локальный';
  const alnum = s.replace(/[^a-zA-Z0-9]/g, '');
  if (!alnum.length) {
    const parts = [];
    for (let i = 0; i < s.length && parts.join('').length < 18; i += 3) {
      parts.push(s.slice(i, i + 3));
    }
    return parts.join('-') || s;
  }
  const parts = [];
  for (let i = 0; i < alnum.length && i < 24; i += 3) {
    parts.push(alnum.slice(i, i + 3));
  }
  return parts.join('-');
}

function resizeImageFileToJpegDataUrl(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) {
          reject(new Error('Размер изображения не определён'));
          return;
        }
        const scale = Math.min(1, AVATAR_MAX_SIDE / Math.max(w, h));
        w = Math.max(1, Math.round(w * scale));
        h = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas недоступен'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', AVATAR_JPEG_QUALITY);
        resolve(dataUrl);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Не удалось загрузить файл'));
    };
    img.src = url;
  });
}

function ProfileSheet({ className, children }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-black/55 bg-gradient-to-b from-[#101c2e] to-[#070d14]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_36px_rgba(0,0,0,0.5)]',
        className
      )}
    >
      {children}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="mb-3 flex items-center gap-2.5 border-b border-white/[0.06] pb-2.5">
      {Icon ? (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-500/35 bg-amber-500/10 text-amber-400/95 shadow-inner">
          <Icon className="h-4 w-4" strokeWidth={2.25} aria-hidden />
        </span>
      ) : null}
      <h2 className="min-w-0 text-[15px] font-semibold leading-tight tracking-tight text-white">{children}</h2>
    </div>
  );
}

/**
 * Экран профиля (хаб): аватар, уникальный ID, прогресс уровня, балансы.
 */
export function LobbyProfileScreen({ user, onBack, onUserPatch, onNotifySuccess, onLogout }) {
  const u = user || {};
  const fileInputRef = useRef(null);
  const roadScrollRef = useRef(null);
  const [avatarError, setAvatarError] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [trophyRoadOpen, setTrophyRoadOpen] = useState(false);
  const [claimedMilestoneIds, setClaimedMilestoneIds] = useState(() => new Set());
  const [coinBurst, setCoinBurst] = useState(null);
  const [claimingMilestoneId, setClaimingMilestoneId] = useState('');
  const [trophyRoadError, setTrophyRoadError] = useState('');
  const burstTimeoutRef = useRef(null);
  const level = Number(u.level) || 1;
  const xpCurrent = Number(u.xpCurrent);
  const xpMax = Number(u.xpMax);
  const xpC = Number.isFinite(xpCurrent) && xpCurrent > 0 ? Math.floor(xpCurrent) : 0;
  const xpM = Number.isFinite(xpMax) && xpMax > 0 ? Math.floor(xpMax) : 100;
  const xpPct = Math.min(100, Math.max(0, (xpC / xpM) * 100));
  const league = getPlayerLeague(level);
  const displayId = formatPublicPlayerId(u.id);
  const rawId = u.id != null ? String(u.id) : '';
  const bucks = u.bucksBalance ?? u.realBalance ?? u.cashBalance ?? 0;
  const balance = u.balance ?? 0;
  const avatarUrl = String(u.avatarUrl || '').trim() || DEFAULT_GUEST_AVATAR_URL;
  const rankLabel = String(u.rankName || '').trim() || rankByLevel(level);
  const roadArtIcons = [Gift, Trophy, Star, Sparkles, Trophy, Gift];
  const profileFieldsReady = [
    Boolean((u.username || '').trim()),
    Boolean((u.email || '').trim()),
    Boolean(rawId),
    Boolean(avatarUrl),
  ].filter(Boolean).length;
  const profileCompleteness = Math.round((profileFieldsReady / 4) * 100);
  const xpToNext = Math.max(0, xpM - xpC);

  const trophyScore = cumulativeRespectPoints(level, xpC);
  const maxRoadTrophies = TROPHY_ROAD_MILESTONES[TROPHY_ROAD_MILESTONES.length - 1]?.trophies || 1;
  const nextRoadMilestone = useMemo(
    () => TROPHY_ROAD_MILESTONES.find((m) => m.trophies > trophyScore),
    [trophyScore]
  );
  const ROAD_CARD_WIDTH = 136;
  const ROAD_CARD_GAP = 16;
  const ROAD_SIDE_PAD = 12;
  const roadCount = TROPHY_ROAD_MILESTONES.length;
  const roadTrackLeft = ROAD_SIDE_PAD + ROAD_CARD_WIDTH / 2;
  const roadTrackWidth = Math.max(0, (roadCount - 1) * (ROAD_CARD_WIDTH + ROAD_CARD_GAP));
  const roadContentWidth = Math.max(
    860,
    ROAD_SIDE_PAD * 2 + roadCount * ROAD_CARD_WIDTH + Math.max(0, roadCount - 1) * ROAD_CARD_GAP
  );
  const roadProgressPx = trophyScoreToRoadProgressPx(trophyScore, ROAD_CARD_WIDTH + ROAD_CARD_GAP);
  const roadProgressPct =
    roadTrackWidth > 0 ? Math.max(0, Math.min(100, (roadProgressPx / roadTrackWidth) * 100)) : 0;
  const roadClaimedCount = TROPHY_ROAD_MILESTONES.filter((m) => trophyScore >= m.trophies).length;
  const roadTotalCount = TROPHY_ROAD_MILESTONES.length;
  const validMilestoneIdSet = useMemo(() => new Set(TROPHY_ROAD_MILESTONES.map((m) => m.id)), []);

  useEffect(() => {
    let cancelled = false;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!rawId || !token) {
      setClaimedMilestoneIds(new Set());
      return undefined;
    }
    if (!trophyRoadOpen) return undefined;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/rewards/trophy-road-state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        const arr = Array.isArray(data.claimedMilestoneIds) ? data.claimedMilestoneIds : [];
        setClaimedMilestoneIds(
          new Set(arr.filter((id) => typeof id === 'string' && validMilestoneIdSet.has(id)))
        );
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rawId, trophyRoadOpen, validMilestoneIdSet]);

  useEffect(() => {
    if (!trophyRoadOpen) setTrophyRoadError('');
  }, [trophyRoadOpen]);

  useEffect(() => {
    return () => {
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
    };
  }, []);
  const roadNeedToNext = nextRoadMilestone ? Math.max(0, nextRoadMilestone.trophies - trophyScore) : 0;
  const rankProgressPct = Math.max(
    0,
    Math.min(100, ((Math.max(1, Math.min(level, MAX_LEVEL)) - 1) / Math.max(1, MAX_LEVEL - 1)) * 100)
  );

  useEffect(() => {
    if (!trophyRoadOpen) return;
    const el = roadScrollRef.current;
    if (!el) return;
    const target = Math.max(0, roadTrackLeft + roadProgressPx - el.clientWidth / 2);
    el.scrollTo({ left: target, behavior: 'smooth' });
  }, [trophyRoadOpen, roadProgressPx, roadTrackLeft]);

  const copyId = useCallback(async () => {
    if (!rawId) return;
    setCopyBusy(true);
    try {
      await navigator.clipboard.writeText(rawId);
      onNotifySuccess?.('Уникальный ID скопирован');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = rawId;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        onNotifySuccess?.('Уникальный ID скопирован');
      } catch {
        setAvatarError('Не удалось скопировать ID');
      }
    } finally {
      setCopyBusy(false);
    }
  }, [rawId, onNotifySuccess]);

  const onPickAvatar = useCallback(() => {
    setAvatarError(null);
    fileInputRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !file.type.startsWith('image/')) {
        if (file) setAvatarError('Выберите файл изображения');
        return;
      }
      if (file.size > 12 * 1024 * 1024) {
        setAvatarError('Файл слишком большой (макс. 12 МБ)');
        return;
      }
      try {
        const dataUrl = await resizeImageFileToJpegDataUrl(file);
        if (dataUrl.length > MAX_DATA_URL_CHARS) {
          setAvatarError('После сжатия изображение всё ещё слишком большое');
          return;
        }
        onUserPatch?.({ avatarUrl: dataUrl });
        onNotifySuccess?.('Аватар сохранён на этом устройстве');
      } catch (err) {
        setAvatarError(err?.message || 'Не удалось обработать изображение');
      }
    },
    [onUserPatch, onNotifySuccess]
  );

  const handleMilestoneClaim = useCallback(
    async (m) => {
      if (!m || !onUserPatch || claimingMilestoneId) return;
      if (trophyScore < m.trophies) return;
      if (claimedMilestoneIds.has(m.id)) return;
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (!token) {
        setTrophyRoadError('Войди в аккаунт, чтобы забрать награду на сервер.');
        return;
      }
      setTrophyRoadError('');
      setClaimingMilestoneId(m.id);
      let data = null;
      try {
        const res = await fetch(`${API_BASE_URL}/api/rewards/trophy-road-claim`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ milestoneId: m.id }),
        });
        data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || 'Сервер не выдал награду');
        }
      } catch (e) {
        setTrophyRoadError(String(e?.message || 'Сбой — попробуй ещё раз'));
        setClaimingMilestoneId('');
        return;
      }

      const chips = Math.max(0, Math.trunc(Number(data?.chips ?? m.chips)) || 0);
      const arr = Array.isArray(data?.claimedMilestoneIds) ? data.claimedMilestoneIds : [];
      setClaimedMilestoneIds(
        new Set(arr.filter((id) => typeof id === 'string' && validMilestoneIdSet.has(id)))
      );
      const nextBal = Math.floor(Number(data?.balance));
      const nextBucks = Math.floor(Number(data?.bucksBalance));
      if (Number.isFinite(nextBal) && Number.isFinite(nextBucks)) {
        onUserPatch({ balance: Math.max(0, nextBal), bucksBalance: Math.max(0, nextBucks) });
      } else {
        const prevChips = Math.max(0, Math.floor(Number(u.balance) || 0));
        const prevBucks = Math.max(0, Math.floor(Number(bucks) || 0));
        onUserPatch({
          balance: prevChips + chips,
          bucksBalance: prevBucks + Math.max(0, Math.trunc(Number(data?.bucks ?? m.bucks)) || 0),
        });
      }

      const particleCount = Math.min(36, 16 + Math.floor(chips / 350));
      setCoinBurst({
        id: `${Date.now()}-${m.id}`,
        particles: createCoinParticles(particleCount),
      });
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
      burstTimeoutRef.current = setTimeout(() => setCoinBurst(null), 2100);
      playDailyRewardSound(0.9 + chips / 7000);
      onNotifySuccess?.('Награда забрана — фишки на сервере');
      setClaimingMilestoneId('');
    },
    [
      bucks,
      claimedMilestoneIds,
      claimingMilestoneId,
      onNotifySuccess,
      onUserPatch,
      trophyScore,
      u.balance,
      validMilestoneIdSet,
    ]
  );

  return (
    <div className="lobby-profile-screen relative mx-auto flex min-h-[100dvh] max-w-5xl flex-col px-3 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(0.5rem,env(safe-area-inset-top,0px))] sm:px-4">
      <CoinRainOverlay burst={coinBurst} coinSrc={DEFAULT_REWARD_COIN_SRC} />
      {/* Фон экрана — только под профилем, без конфликта z-index с корнем App */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 min-h-[100dvh] overflow-hidden"
        aria-hidden
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a121f] via-[#060b10] to-[#030508]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(201,162,39,0.12),transparent_55%)]" />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-hidden
        onChange={onFileChange}
      />

      <header className="mb-5 shrink-0">
        <ProfileSheet className="p-1.5 sm:p-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onBack}
              className="lobby-focus flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-black/50 bg-gradient-to-b from-[#1a2d4a] to-[#0f1a2e] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition hover:from-[#1f3560] hover:to-[#122030] active:scale-[0.98]"
              aria-label="Назад"
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
            </button>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-rose-400/35 bg-black/35 px-3 py-2 text-sm font-semibold text-slate-100 shadow-inner">
                <Sparkles className="h-3.5 w-3.5 text-rose-300/90" strokeWidth={2} aria-hidden />
                Досье авторитета
              </span>
              <button
                type="button"
                onClick={() => setTrophyRoadOpen((v) => !v)}
                className="lobby-focus inline-flex items-center gap-1 rounded-lg border border-rose-400/30 bg-rose-950/20 px-2 py-1 text-xs font-semibold text-rose-100/90 transition hover:border-rose-300/45 hover:bg-rose-900/25"
              >
                Воровская тропа
                {trophyRoadOpen ? <ChevronUp className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
              </button>
            </div>
            <button
              type="button"
              className="lobby-focus flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/25 text-white/35 transition hover:bg-black/35 hover:text-white/50"
              aria-label="Настройки (скоро)"
              title="Скоро"
              onClick={(e) => e.preventDefault()}
            >
              <Settings className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        </ProfileSheet>
      </header>

      {trophyRoadOpen ? (
        <ProfileSheet className="mb-5 p-4 sm:p-5">
          <SectionTitle icon={Trophy}>Воровская тропа</SectionTitle>
          <div className="relative overflow-hidden rounded-2xl border border-rose-400/25 bg-gradient-to-b from-[#1b0b10] via-[#12070b] to-[#080407] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] sm:p-4">
            <div className="pointer-events-none absolute inset-0 opacity-25" aria-hidden>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(190,24,93,0.15),transparent_34%),radial-gradient(circle_at_82%_22%,rgba(153,27,27,0.12),transparent_30%)]" />
            </div>
            <div className="relative z-[1] mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-black text-rose-100">Следующий служебный ранг</p>
                <p className="text-xs text-rose-100/75">
                  {nextRoadMilestone
                    ? `${nextRoadMilestone.title} при ${nextRoadMilestone.trophies} респекта`
                    : 'Линейка рангов закрыта полностью'}
                </p>
              </div>
              <div className="rounded-xl border border-rose-300/30 bg-black/45 px-3 py-1.5 text-right">
                <p className="text-[10px] uppercase tracking-wide text-rose-100/65">Фонд респекта</p>
                <p className="text-lg font-black tabular-nums text-rose-100">{trophyScore}</p>
              </div>
            </div>
            {trophyRoadError ? (
              <div className="relative z-[1] mb-2 rounded-lg border border-red-400/35 bg-red-950/40 px-2 py-1.5 text-xs text-red-100">
                {trophyRoadError}
              </div>
            ) : null}

            <div ref={roadScrollRef} className="lobby-mp-scroll relative z-[1] overflow-x-auto pb-2">
              <div className="relative pb-8 pt-2" style={{ width: `${roadContentWidth}px` }}>
                <div
                  className="absolute top-[126px] h-2 rounded-full bg-[#130a0e]/90 ring-1 ring-black/45"
                  style={{ left: `${roadTrackLeft}px`, width: `${roadTrackWidth}px` }}
                />
                <div
                  className="absolute top-[126px] h-2 rounded-full bg-gradient-to-r from-rose-700 via-red-700 to-rose-500"
                  style={{ left: `${roadTrackLeft}px`, width: `${roadProgressPx}px` }}
                />
                <div
                  className="absolute top-[112px] z-[2] -translate-x-1/2 transition-all duration-300"
                  style={{ left: `${roadTrackLeft + roadProgressPx}px` }}
                >
                  <div className="relative">
                    <span className="relative inline-flex h-8 items-center gap-1 rounded-full border border-rose-300/45 bg-[#12080c] px-2 text-[11px] font-black tabular-nums text-rose-100 shadow-[0_6px_14px_rgba(0,0,0,0.45)]">
                      {trophyScore}
                      <span aria-hidden>🏆</span>
                    </span>
                  </div>
                </div>

                <div
                  className="flex items-end gap-4"
                  style={{ paddingLeft: ROAD_SIDE_PAD, paddingRight: ROAD_SIDE_PAD }}
                >
                  {TROPHY_ROAD_MILESTONES.map((m, idx) => {
                    const unlocked = trophyScore >= m.trophies;
                    const claimed = claimedMilestoneIds.has(m.id);
                    const canClaim = unlocked && !claimed;
                    const claimLabel =
                      claimingMilestoneId === m.id
                        ? '…'
                        : !unlocked
                          ? 'Закрыто'
                          : claimed
                            ? 'Склеймлено'
                            : 'Клеймить';
                    const ArtIcon = roadArtIcons[idx % roadArtIcons.length] || Gift;
                    const transitionTarget = KEY_TRANSITION_RANKS[m.level] || '';
                    const isTransitionRank = Boolean(transitionTarget);
                    return (
                      <div key={m.id} className="w-[136px] shrink-0 text-center">
                        <div
                          className={cn(
                            'relative mx-auto mb-3 h-[92px] w-[136px] overflow-hidden rounded-2xl border p-2.5 shadow-[0_8px_20px_rgba(0,0,0,0.35)]',
                            isTransitionRank &&
                              (unlocked
                                ? 'ring-2 ring-rose-300/55 shadow-[0_0_12px_rgba(190,24,93,0.26)]'
                                : 'ring-2 ring-red-300/40 shadow-[0_0_10px_rgba(153,27,27,0.22)]'),
                            unlocked
                              ? 'border-rose-300/55 bg-gradient-to-b from-[#2a0d15]/90 to-[#13070c]/95'
                              : 'border-slate-500/35 bg-gradient-to-b from-[#17191f]/90 to-[#0a0b0f]/95'
                          )}
                        >
                          {!unlocked ? (
                            <span className="absolute right-1.5 top-1.5 rounded-md border border-rose-300/35 bg-[#2b0d15]/80 px-1.5 py-0.5 text-[9px] font-bold text-rose-100/90">
                              ЗАКРЫТО
                            </span>
                          ) : null}
                          {isTransitionRank ? (
                            <span className="absolute left-1/2 top-1.5 -translate-x-1/2 rounded-md border border-rose-300/35 bg-[#2b0d15]/80 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-rose-100/90">
                              КЛЮЧЕВОЙ
                            </span>
                          ) : null}
                          <span className="absolute left-1.5 top-1.5 rounded-md border border-slate-300/30 bg-black/50 px-1.5 py-0.5 text-[9px] font-black text-slate-100">
                            РАНГ {m.level}
                          </span>
                          <div className="mb-1 flex h-10 items-center justify-center">
                            <ArtIcon className="h-8 w-8 text-rose-100 drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]" strokeWidth={2.1} aria-hidden />
                          </div>
                          {isTransitionRank ? (
                            <p className="truncate text-[10px] font-black uppercase tracking-wide text-rose-100">
                              Следующий статус: {transitionTarget}
                            </p>
                          ) : (
                            <p className="truncate text-[10px] font-bold tracking-wide text-slate-100">{m.title}</p>
                          )}
                          {isTransitionRank ? (
                            <p className="truncate text-[9px] text-slate-300/80">{m.title}</p>
                          ) : null}
                          <p className="mt-1 text-xs font-semibold text-slate-200">+{m.chips} фишек в кассу</p>
                          <p className="text-[11px] font-semibold text-slate-300/90">
                          {m.bucks ? `+${m.bucks} баксов` : 'Без долларов'}
                          </p>
                        </div>

                        <div className="mb-1 h-2 w-px bg-black/60 mx-auto" />
                        <p className="text-sm font-black tabular-nums text-slate-100">{m.trophies} респ.</p>
                        <button
                          type="button"
                          disabled={!canClaim || Boolean(claimingMilestoneId)}
                          onClick={() => (canClaim && !claimingMilestoneId ? void handleMilestoneClaim(m) : undefined)}
                          className={cn(
                            'lobby-focus mt-1 inline-flex min-w-[96px] items-center justify-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition',
                            canClaim
                              ? 'border-rose-200/50 bg-gradient-to-b from-rose-900/50 to-rose-950/60 text-rose-50 hover:from-rose-800/55 hover:to-rose-900/65'
                              : claimed
                                ? 'border-slate-500/35 bg-black/50 text-slate-400/90'
                                : 'border-slate-400/25 bg-black/45 text-slate-300/55'
                          )}
                        >
                          <Gift className="h-3 w-3" aria-hidden />
                          {claimLabel}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="absolute bottom-0 left-3 rounded-md border border-rose-300/25 bg-black/55 px-2 py-0.5">
                  <p className="text-[11px] font-black tabular-nums text-rose-100/90">
                    Фонд: {trophyScore} респекта · Ранги 1–15 · прокрутка влево/вправо
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ProfileSheet>
      ) : null}

      <div className="grid flex-1 grid-cols-1 gap-5 lg:grid-cols-[minmax(0,300px)_1fr] lg:gap-6">
        {/* Левая колонка — одна карточка, внутри «слои» без лишних рамок */}
        <ProfileSheet className="p-3.5 sm:p-4.5">
          <div className="relative">
            <div className="pl-[104px] sm:pl-[118px]">
              <h1 className="truncate text-xl font-semibold tracking-tight text-white sm:text-2xl">{u.username || 'Гость'}</h1>
              {u.email ? <p className="mt-1 truncate text-sm text-white/55">{u.email}</p> : null}

              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="inline-flex items-center rounded-md border border-amber-500/35 bg-amber-500/10 px-2 py-1 text-[11px] font-semibold text-amber-100/95">
                  УР. {level}
                </span>
                <span className="inline-flex items-center rounded-md border border-violet-500/35 bg-violet-500/10 px-2 py-1 text-[11px] font-semibold text-violet-100/95">
                  {rankLabel}
                </span>
                <span className="inline-flex items-center rounded-md border border-sky-500/35 bg-sky-500/10 px-2 py-1 text-[11px] font-semibold text-sky-100/95">
                  Досье {profileCompleteness}%
                </span>
              </div>
            </div>

            <div className="absolute left-0 top-0">
              <span className="absolute -left-1 -top-1 z-[3] inline-flex h-8 w-8 items-center justify-center">
                <Star className={cn('absolute inset-0 h-8 w-8 drop-shadow-md', league.starClass)} strokeWidth={1.2} aria-hidden />
                <span className="relative z-[1] text-[8px] font-black tabular-nums text-white">{level}</span>
              </span>
              <div className="rounded-2xl border-[3px] border-amber-500/90 bg-gradient-to-br from-amber-600/45 via-amber-800/50 to-amber-950/90 p-[3px] shadow-[0_6px_24px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.2)]">
                <div className="flex h-[88px] w-[88px] items-center justify-center overflow-hidden rounded-[13px] bg-gradient-to-br from-[#1e3a5f] to-[#0c1624] text-3xl font-bold text-white sm:h-[100px] sm:w-[100px]">
                  <img
                    src={avatarUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    draggable={false}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_GUEST_AVATAR_URL;
                    }}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={onPickAvatar}
                className="lobby-focus absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-sky-400/90 bg-gradient-to-b from-sky-500 to-sky-700 text-white shadow-lg transition hover:from-sky-400 hover:to-sky-600 active:scale-95"
                aria-label="Сменить аватар"
                title="Загрузить фото"
              >
                <Pencil className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>
          {avatarError ? (
            <p className="mt-2 text-xs text-red-400/95" role="alert">
              {avatarError}
            </p>
          ) : null}

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-2 py-1.5">
              <p className="text-[10px] uppercase text-white/45">Общак</p>
              <p className="mt-0.5 text-sm font-black tabular-nums text-amber-100">{xpC}</p>
            </div>
            <div className="rounded-lg border border-sky-500/25 bg-sky-500/10 px-2 py-1.5">
              <p className="text-[10px] uppercase text-white/45">Касса</p>
              <p className="mt-0.5 text-sm font-black tabular-nums text-sky-100">{balance}</p>
            </div>
            <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-2 py-1.5">
              <p className="text-[10px] uppercase text-white/45">Нал</p>
              <p className="mt-0.5 text-sm font-black tabular-nums text-emerald-100">{bucks}</p>
            </div>
          </div>

          <div className="mt-3 space-y-2 rounded-xl border border-amber-400/20 bg-gradient-to-b from-black/55 to-black/30 p-2.5 ring-1 ring-black/60 sm:p-3">
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-[11px] text-white/55">До следующего ранга</p>
                <p className="text-[11px] tabular-nums text-white/60">
                  {xpToNext} респ. ({xpC}/{xpM})
                </p>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-black/55 ring-1 ring-black/45">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-orange-500 shadow-[0_0_10px_rgba(251,191,36,0.45)]" style={{ width: `${xpPct}%` }} />
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-[11px] text-white/55">Воровская тропа</p>
                <p className="text-[11px] tabular-nums text-white/60">
                  {trophyScore}/{maxRoadTrophies}
                </p>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-black/55 ring-1 ring-black/45">
                <div className="h-full rounded-full bg-gradient-to-r from-sky-300 via-cyan-300 to-indigo-400 shadow-[0_0_10px_rgba(56,189,248,0.4)]" style={{ width: `${roadProgressPct}%` }} />
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-[11px] text-white/55">Путь до «Вора»</p>
                <p className="text-[11px] tabular-nums text-white/60">
                  Ур. {level}/{MAX_LEVEL}
                </p>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-black/55 ring-1 ring-black/45">
                <div className="h-full rounded-full bg-gradient-to-r from-violet-300 via-fuchsia-300 to-rose-400 shadow-[0_0_10px_rgba(217,70,239,0.38)]" style={{ width: `${rankProgressPct}%` }} />
              </div>
            </div>
          </div>

          <div className="mt-3 space-y-0 overflow-hidden rounded-xl border border-white/[0.07] bg-black/35 ring-1 ring-black/40">
            <div className="flex items-start justify-between gap-3 border-b border-white/[0.06] px-3 py-3 sm:px-4">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-white/40">Уникальный ID</p>
                <p className="mt-1 break-all font-mono text-[13px] leading-snug text-amber-100/90">{displayId}</p>
              </div>
              <button
                type="button"
                onClick={copyId}
                disabled={!rawId || copyBusy}
                className="lobby-focus mt-0.5 flex shrink-0 items-center justify-center rounded-lg border border-sky-500/40 bg-sky-600/85 p-2.5 text-white shadow-md transition hover:bg-sky-500 disabled:opacity-40"
                aria-label="Копировать уникальный ID"
                title="Копировать"
              >
                <Copy className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 px-3 py-3 sm:px-4">
              <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-2">
                <p className="text-[10px] font-medium text-white/40">Страна</p>
                <p className="mt-0.5 text-xs text-white/75">Скоро</p>
              </div>
              <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-2">
                <p className="text-[10px] font-medium text-white/40">Клуб</p>
                <p className="mt-0.5 text-xs text-white/50">Нет клуба · скоро</p>
              </div>
            </div>
          </div>
        </ProfileSheet>

        {/* Правая колонка: только уникальные данные без дублей */}
        <div className="flex flex-col gap-5">
          <ProfileSheet className="p-3.5 sm:p-4">
            <SectionTitle icon={Trophy}>Воровская сводка</SectionTitle>
            <div className="grid grid-cols-2 gap-2.5 rounded-xl border border-white/[0.06] bg-[#04080e]/90 p-2.5 sm:grid-cols-3">
              <CompactStat label="Тропа авторитета" value={`${Math.round(roadProgressPct)}%`} emphasize />
              <CompactStat label="До ранга" value={nextRoadMilestone ? `${roadNeedToNext} респ.` : 'Предел'} />
              <CompactStat label="Открыто рангов" value={`${roadClaimedCount}/${roadTotalCount}`} />
              <CompactStat label="Ближайший подгон" value={nextRoadMilestone ? nextRoadMilestone.title : '—'} wide />
              <CompactStat label="VIP" value="Скоро" muted />
              <CompactStat label="Турниры" value="Скоро" muted />
            </div>
          </ProfileSheet>

          <div className="rounded-xl border border-dashed border-white/10 bg-black/25 px-4 py-3 text-center text-sm text-white/40">
            Коллекции — скоро
          </div>
        </div>
      </div>

      {typeof onLogout === 'function' ? (
        <div className="mt-8 flex justify-center border-t border-white/10 pt-6">
          <button
            type="button"
            onClick={onLogout}
            className="lobby-focus inline-flex items-center gap-2 rounded-xl border border-red-500/35 bg-red-950/25 px-4 py-2.5 text-sm font-medium text-red-100/90 shadow-sm transition hover:bg-red-950/40"
          >
            <LogOut className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
            Выйти из аккаунта
          </button>
        </div>
      ) : null}
    </div>
  );
}

function CompactStat({ label, value, muted, emphasize, wide }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-2.5 py-2',
        wide ? 'col-span-2 sm:col-span-1' : 'col-span-1',
        muted ? 'border-white/[0.06] bg-white/[0.02]' : 'border-white/[0.08] bg-white/[0.03]',
        emphasize && 'border-amber-500/35 bg-amber-500/[0.08]'
      )}
    >
      <span className={cn('block text-[10px] uppercase tracking-wide text-white/45', muted && 'text-white/35')}>{label}</span>
      <span
        className={cn(
          'mt-1 block truncate text-[12px] font-semibold tabular-nums text-white',
          emphasize && 'text-amber-100',
          muted && 'text-white/45 font-normal'
        )}
      >
        {value}
      </span>
    </div>
  );
}
